/**
 * THE DESK — the railway, what fell off it, and who is waiting.
 *
 * The railway is the point of this screen. Each stage shows what is
 * sitting in it AND how much of that has been sitting too long, because a
 * funnel built from status alone always looks healthy: every row is
 * somewhere. Forty jobs in progress with none stuck is a busy shop. Three
 * with all three stuck is a hole.
 *
 * Underneath it is the part no dashboard here has ever had — what left
 * the railway without reaching the end. Two of those four lines are money
 * the shop has already earned and never asked for.
 */

import {
  funnel, fallout, spanFrom, spanLabel, stepSpan, canStepSpan,
  SPANS, type Stage, type Span,
} from "../data/funnel.ts";
import { listJobs, vehicleName, STATUS_LABEL, type Status } from "../data/jobs.ts";
import { stateOf } from "./jobs.ts";
import { db } from "../data/client.ts";
import { IMPORT_WATERMARK } from "../domain/watermark.ts";
import { myDay } from "../data/myday.ts";
import { picker } from "../ui/picker.ts";
import { dayMap } from "./daymap.ts";
import { esc, money, ago, mountTo, since } from "../ui/dom.ts";
import { thumbHtml, fillThumbs } from "../ui/carthumb.ts";
import { firstDay } from "./firstday.ts";

/**
 * The window the railway is counting over. The SAME control the schedule
 * uses, from the same list, in the same place on the left — two controls
 * that mean the same thing are two things to learn and two places for them
 * to drift apart.
 *
 * It survives leaving the page and coming back, because somebody who has
 * set the desk to "this week" means it.
 */
/**
 * OPENS ON THIS MONTH. It opened on Today, after Anthony's "autoload to
 * day in each viewpoint from the dash", and on Today the railway could
 * never show a thing stuck. A stage's stuck count is only jobs raised
 * inside the window, and stuck means older than two, three, seven or
 * thirty days, so every amber number on Today was zero by construction.
 * The one part of this screen that exists to be read first thing was
 * always blank first thing. Anthony, 2026-09-24: "open the desk on this
 * month." The day map below it still answers "what is happening now",
 * and Today is one tap away on the picker.
 */
let span: Span = "month";

/**
 * WHICH today, which week, which month.
 *
 * The span says how big the window is; this says where it sits. Before
 * this existed the arrows on the control had nothing to move, so they
 * cycled the option list instead and pressing "next" on Today gave you
 * This week. Anthony: "it just switched day/week/month/all time instead
 * of switching between yesterday/today/tomorrow."
 *
 * Reset to now whenever the UNIT changes, deliberately. Somebody who has
 * walked back to last Tuesday and then asks for This month means this
 * month -- carrying the anchor over would land them in a month they did
 * not ask for, and the control would say "September" when they pressed
 * something labeled "This month".
 */
let anchor: Date = new Date();

function stage(s: Stage, last: boolean): string {
  return `
    <a class="rail-stage ${s.stuck ? "hot" : ""}" href="${s.href}">
      <div class="rail-n">${s.count}</div>
      <div class="rail-l">${esc(s.label)}</div>
      ${s.stuck ? `<div class="rail-stuck">${s.stuck} stuck</div>` : `<div class="rail-ok">clear</div>`}
    </a>${last ? "" : `<div class="rail-link" aria-hidden="true"></div>`}`;
}

export async function dashboardPage(name: string): Promise<void> {
  const p = picker(SPANS, span, {
    step: true,
    nowLabel: spanLabel(span, anchor),
    canStep: canStepSpan(span),
  });
  /* THE MIDDLE, BY NAME. This screen is no longer somewhere you navigate
     to — it is what the side panel opens on top of — so it paints `#app`
     directly rather than wherever the router happens to be pointing. */
  mountTo("app", `<div class="page">
      <header class="page-head"><h1>Good to see you, ${esc(name.split(" ")[0] ?? name)}</h1>
        <p>Where the work is, and where it stopped.</p></header>
      <!-- THE FIRST DAY. Until a new shop has added a customer, sent a
           quote and pasted its review link, the desk opens with those
           three. Empty for everybody else. See src/pages/firstday.ts. -->
      <div id="firstday"></div>
      <div class="listbar right">
        <span class="pager-note" id="railnote"></span>${p.html}</div>
      <div class="card pad" id="rail"><div class="loading">Counting&hellip;</div></div>
      <!-- WHERE TODAY'S WORK IS. This screen now holds the middle of the
           app on its own — every other screen opens in the panel at the
           edge — so it has the room for the one thing a mobile shop asks
           all morning and answers out of memory: who is nearest to that.
           See src/pages/daymap.ts. -->
      <div id="daymap"></div>
      <!-- WHAT THIS PERSON HAS DONE TODAY. Quiet, no target, no bar to
           fill: a counter that judges is a counter people learn to game.
           See src/data/myday.ts. -->
      <div id="tally"></div>
      <!-- WHAT IS ON THIS PERSON, right under the railway, and the
           quotes nobody has answered beside it.

           Anthony: "put the Quotes waiting on a yes right under on you on
           the desk feed. Side by side is find too on desktop only."

           They are the same question asked twice -- what have I got, and
           who owes me an answer -- so they read as one band. Stacked on a
           phone, two columns from 900px up. An empty #mine collapses when
           somebody has nothing assigned, so an advisor gets one full-width
           card rather than a blank half. -->
      <div class="deskpair">
        <div id="mine"></div>
        <div class="card" id="waiting"><div class="loading">Reading&hellip;</div></div>
      </div>
      <div class="card" id="fell"></div>
    </div>`);

  p.bind(
    // The middle picks the unit, and lands you on the current one.
    (k) => { span = k; anchor = new Date(); void dashboardPage(name); },
    // The arrows move in that unit. A day, a week or a month, decided
    // here because only this page knows the span is a date range.
    (dir) => { anchor = stepSpan(span, anchor, dir); void dashboardPage(name); },
  );

  /* A shop with no customer yet has nothing to be stuck, and a trophy
     for that reads as the software not working. Started first, awaited
     only where the trophies are drawn. */
  const fresh = firstDay("firstday");
  void mineBand();
  void dayTally();
  void dayMap("daymap");

  try {
    const [stages, left, quotes] = await Promise.all([
      funnel(span, anchor), fallout(spanFrom(span, anchor)),
      listJobs({ statuses: ["estimate"], limit: 200 }),
    ]);
    const isFresh = await fresh;
    const NOTHING_YET = "Nothing here yet. It fills in as your first jobs come through.";

    const note = document.getElementById("railnote");
    if (note) {
      /* Reads the PERIOD, not the option that was picked. It used to say
         "raised today" whatever the arrows had been doing, which on a
         screen whose whole job is a count for a window is the number
         lying about where it came from. */
      note.textContent = span === "all"
        ? "everything on the books"
        : `raised ${spanLabel(span, anchor).toLowerCase()}`;
    }

    const rail = document.getElementById("rail");
    if (rail) {
      const notes = stages.filter((s) => s.note);
      rail.innerHTML =
        `<div class="rail">${stages.map((s, i) => stage(s, i === stages.length - 1)).join("")}</div>` +
        (notes.length
          ? `<div class="rail-notes">${notes.map((s) =>
              `<div><b>${esc(s.label)}</b> — ${esc(s.note ?? "")}</div>`).join("")}</div>`
          : `<div class="rail-notes clear">${isFresh ? NOTHING_YET : "Nothing is stuck anywhere. &#127942;"}</div>`);
    }

    const fell = document.getElementById("fell");
    if (fell) {
      /* A row with nothing in the window but something over all time is
         still worth showing, quietly: "none this week, 44 all time" is a
         useful sentence and an empty card is not. */
      const real = left.filter((f) => f.count > 0 || f.ever > 0);
      fell.innerHTML = `<div class="card-head">What left the railway</div>` + (real.length
        ? real.map((f) => `
            <a class="row" href="${f.href}">
              <div class="row-main"><div class="row-title">
                <span class="num ${f.money ? "bad" : ""}">${f.count}</span>
                <span class="fell-l">${esc(f.label)}</span>
                ${f.ever > f.count
                  ? `<span class="fell-ever">${f.ever} all time</span>` : ""}</div></div>
              <div class="row-side"><div class="when">${
                f.money ? "worth chasing"
                : f.key === "unbilled" ? "cash, or never asked for"
                : "closed on purpose"}</div></div>
            </a>`).join("")
        : `<div class="empty">${isFresh ? NOTHING_YET : "Nothing has fallen out. <span>&#127942;</span>"}</div>`);
    }

    /**
     * WAITING ON A YES MEANS IT WENT OUT.
     *
     * This drew the first eight estimates of any kind and called all of
     * them "waiting on a yes", under a row that said "sent N ago" using
     * created_at. Most of them had never been sent to anybody, so the card
     * invented an answer the shop was owed and dated it from the day
     * somebody typed the price.
     *
     * Only `sent` now: a price that has gone and come back with nothing.
     * Approved, cancelled and changes-asked-for are answers, and a quote
     * nobody sent is not unanswered, it is undelivered -- so the unsent
     * pile is COUNTED underneath rather than dropped, because it is the
     * bigger problem and it needs a person, not a text.
     */
    const host = document.getElementById("waiting");
    const sent = quotes.filter((j) => stateOf(j) === "sent");
    const unsent = quotes.filter((j) => stateOf(j) === "not_sent");
    if (host) {
      host.innerHTML = `<div class="card-head">Quotes waiting on a yes</div>` + (sent.length
        ? sent.slice(0, 8).map((j) => `
            <a class="row" href="#/job/${j.id}">
              ${thumbHtml(j.id, j.vehicles)}
              <div class="row-main">
                <div class="row-title"><span class="num">${esc(j.job_number)}</span>
                  <span class="pill q-sent">Sent</span></div>
                <div class="row-sub">${esc(j.customers?.full_name ?? "No customer")}
                  <span class="dot">&middot;</span>${esc(vehicleName(j.vehicles))}</div>
              </div>
              <div class="row-side">
                <div class="amount">${money(j.totals.total)}</div>
                <div class="when">${esc(since(j.created_at))}</div>
              </div>
            </a>`).join("")
        : `<div class="empty">${isFresh ? NOTHING_YET : "Nobody is sitting on a price we sent. <span>&#127942;</span>"}</div>`)
        + (unsent.length
          ? `<a class="row quiet-row" href="#/quotes">
               <div class="row-main"><div class="row-sub">${unsent.length} more
                 ${unsent.length === 1 ? "quote is" : "quotes are"} priced and have
                 never been sent to anybody.</div></div>
               <div class="row-side"><div class="when">open Quotes</div></div>
             </a>`
          : "");
      void fillThumbs(host);
    }
  } catch (e) {
    mountTo("app", `<div class="page"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`);
  }
}

/**
 * WHAT IS ON YOU — under the railway, where the day starts.
 *
 * Anthony: "the everything assigned to you box on the schedule page should
 * live on the desk page, right under the railway basically. A summary
 * dashboard of what's left for that persons login to take care of and
 * outstanding etc."
 *
 * It was on the schedule, which was the wrong screen for it. The railway
 * above answers "where is the SHOP stuck". This answers "where am I
 * stuck", and those two questions are asked in the same breath by the same
 * person at the same moment — so they belong on the same screen, in that
 * order.
 *
 * A calendar answers "what is happening on this day". This is a LIST, not
 * a date: everything open with this person's name on it, whatever day it
 * says.
 *
 * ─── THE ORDER, AND THE WINDOW ─────────────────────────────────────────
 *
 * Anthony: "on you tasks should only be from the last 7 days really with a
 * load more option. Also organize into the most recent things that need
 * attention."
 *
 * It was oldest first, which sounds right and is not. Oldest first fills
 * the top of the box with the jobs that have sat longest precisely BECAUSE
 * nobody is going to do them, and buries this morning's work underneath.
 *
 * So it sorts by HOW RECENTLY A JOB MOVED, newest first, which is the
 * standing rule everywhere else in here: "in all areas that we sort jobs,
 * leads, schedule etc, I always want the most recent to show first."
 *
 * The first pass sorted by what kind of trouble a job was in and used
 * recency only to break ties. That sounds better and reads worse: a
 * fortnight-old overdue job pinned itself to the top of the box every
 * morning and this morning's work sat underneath it. The trouble is not
 * lost -- it is a red pill on the row and a count in the summary line
 * above, which is where somebody scanning for it looks anyway. Trouble now
 * only breaks a tie between two jobs that moved at the same moment.
 *
 * SEVEN DAYS, AND THE REST BEHIND A BUTTON. A job that has not moved in a
 * week is not what somebody opening this at eight in the morning needs to
 * see. The count of what is older is SAID rather than hidden, because a
 * list that quietly stops short is how work goes missing — that is the
 * whole reason this box exists.
 *
 * IT DRAWS FOR ANYBODY WITH WORK ON THEM, not only technicians. An advisor
 * who has been assigned a job has the same question, and somebody with
 * nothing assigned gets nothing rather than an empty box.
 */
async function mineBand(): Promise<void> {
  const host = document.getElementById("mine");
  if (!host) return;

  try {
    const sb = await db();
    const { data: me } = await sb.auth.getUser();
    if (!me.user?.id) return;

    // Jobs hang off a technician row, and a person can have a login and no
    // hands. That is not an error, it is an advisor.
    const { data: tech } = await sb.from("technicians")
      .select("id").eq("profile_id", me.user.id).limit(1).maybeSingle();
    const techId = (tech as { id: string } | null)?.id;
    if (!techId) { host.innerHTML = ""; return; }

    type Job = {
      id: string; job_number: string; status: string; scheduled_at: string | null;
      updated_at: string | null; created_at: string | null;
      customers: { full_name: string } | null;
      vehicles: { id: string; year: number | null; make: string | null; model: string | null;
                  trim: string | null; description: string | null } | null;
      line_items: Array<{ total: number }> | null;
      /** The second opinion on whether this job is finished. See isLate. */
      invoices: Array<{ id: string; status: string }> | null;
    };
    type Row = { jobs: Job | null };

    const { data, error } = await sb.from("job_technicians")
      .select("jobs!inner(id,job_number,status,scheduled_at,updated_at,created_at," +
              "customers(full_name),vehicles(id,year,make,model,trim,description)," +
              "line_items(total),invoices(id,status))")
      .eq("technician_id", techId)
      // `paid` is in the list now: finished work belongs on this card,
      // underneath what is still open. See below.
      .in("jobs.status",
        ["estimate", "scheduled", "in_progress", "completed", "invoiced", "paid"])
      /**
       * THE MIGRATION IS NOT ON YOU.
       *
       * Anthony, repeatedly: "this section is not ordered correctly."
       *
       * It was not an ordering fault, which is why fixing the sort three
       * times never fixed it. 1,328 of the 1,340 rows this card was
       * holding came from the migration -- 402 of them "completed", which
       * is where "41 finished, not billed" came from, and 926 "paid",
       * which is where "78 finished this week" came from. Twelve rows were
       * real work.
       *
       * And because every imported row carries the SAME timestamp, sorting
       * by recency had nothing to sort by. Every comparison tied and fell
       * through to the trouble tier, which put four hundred completed rows
       * on top. The order was not wrong; there was no order to have.
       *
       * The floor went on the railway, the leads and the job lists when it
       * was added. This card was missed.
       */
      .gte("jobs.created_at", IMPORT_WATERMARK)
      .eq("jobs.is_training", false)
      .limit(200);
    if (error) throw new Error(error.message);

    const all = ((data ?? []) as unknown as Row[])
      .map((r) => r.jobs)
      .filter((j): j is Job => Boolean(j));

    if (!all.length) { host.innerHTML = ""; return; }

    /**
     * WHAT IS LEFT TO DO, counted before the list. Somebody opening this
     * wants the shape of their day in one glance and the detail second.
     * "Waiting on a yes" and "finished, not billed" are the two that cost
     * money by sitting still.
     */
    const now = Date.now();
    const lineTotal = (j: Job) =>
      (j.line_items ?? []).reduce((n, l) => n + Number(l.total ?? 0), 0);
    /**
     * A JOB WITH MONEY AGAINST IT IS NOT LATE, WHATEVER THE STATUS SAYS.
     *
     * Anthony: "IF DAY HAS GONE AND JOB IS DONE THEN SHOULD SAY PAID OR
     * SOMETHING NOT day has gone."
     *
     * The status column alone cannot be trusted for this. A job gets
     * finished in the bay and paid at the curb and nobody moves it off
     * `scheduled` -- and then a card shouts that its day has gone at
     * somebody who knows perfectly well it is done. Once that happens
     * twice the pill means nothing and gets ignored, which is how the last
     * flag on this screen died.
     *
     * An invoice is the second opinion. A job carrying one has been billed
     * whatever the status believes, so it says Billed or Paid instead. It
     * can still be BEHIND -- that is what the "finished, not billed" and
     * "billed, not paid" counts are for -- but it is never accused of not
     * having been started.
     */
    const billed = (j: Job) => (j.invoices ?? []).length > 0;
    const settled = (j: Job) => (j.invoices ?? []).some((i) => i.status === "paid");

    const isLate = (j: Job) => j.status === "scheduled" && Boolean(j.scheduled_at)
      && Date.parse(j.scheduled_at as string) < now
      && !billed(j);

    /**
     * WHAT KIND OF TROUBLE, lowest first. The order is the order somebody
     * would work the list: a customer standing waiting, then money that
     * has stopped moving, then everything merely open.
     */
    const trouble = (j: Job): number =>
      isLate(j) ? 0
      : j.status === "completed" ? 1        // done, nobody billed it
      : j.status === "estimate" ? 2         // waiting on a yes
      : j.status === "invoiced" ? 3         // billed, not paid
      : 4;

    /**
     * WHEN IT LAST MATTERED. The later of the day it is booked for and the
     * day somebody last touched it, so a job booked for next Tuesday and a
     * job edited this morning are both recent, and neither is judged by
     * the day it happened to be created.
     */
    const touched = (j: Job): number => Math.max(
      j.scheduled_at ? Date.parse(j.scheduled_at) : 0,
      j.updated_at ? Date.parse(j.updated_at) : 0,
      j.created_at ? Date.parse(j.created_at) : 0);

    const mine = [...all].sort((a, b) =>
      touched(b) - touched(a) || trouble(a) - trouble(b));

    /**
     * WHAT IS STILL ON YOU, THEN WHAT YOU HAVE FINISHED.
     *
     * Anthony: "show the most recent things that are on you to do. When
     * they are cleared put them under the uncleared ones. but in green as
     * finished."
     *
     * The card only ever held open work, so finishing something made it
     * vanish — and a list that empties as you work it gives nothing back.
     * Seeing the four you cleared this week sitting under the two you have
     * left is the difference between a to-do list and a record of a day.
     *
     * Paid is the only finished state here. Completed and invoiced are
     * still ON you: the work is done and the money is not, which is
     * exactly the pile that goes quiet.
     */
    const isDone = (j: Job) => j.status === "paid";
    const doing = mine.filter((j) => !isDone(j));
    const done = mine.filter(isDone);

    const WEEK = 7 * 864e5;
    const recent = (j: Job) => now - touched(j) <= WEEK;
    const fresh = doing.filter(recent);
    const freshDone = done.filter(recent);
    const older = [...doing.filter((j) => !recent(j)), ...done.filter((j) => !recent(j))];

    const quotes = doing.filter((j) => j.status === "estimate");
    const late = doing.filter(isLate);
    const unbilled = doing.filter((j) => j.status === "completed");
    const owed = doing.filter((j) => j.status === "invoiced")
      .reduce((n, j) => n + lineTotal(j), 0);

    const line = (j: Job) => `
      <a class="row${isLate(j) ? " late" : ""}${isDone(j) ? " settled" : ""}"
        href="#/job/${esc(j.id)}">
        ${thumbHtml(j.id, j.vehicles)}
        <div class="row-main">
          <div class="row-title"><span class="num">${esc(j.job_number)}</span>
            <span class="pill s-${esc(j.status)}">${
              esc(STATUS_LABEL[j.status as Status] ?? j.status)}</span>
            ${isLate(j) ? `<span class="pill late">day has gone</span>`
              : settled(j) && j.status !== "paid" ? `<span class="pill s-paid">paid</span>`
              : billed(j) && j.status === "scheduled"
                ? `<span class="pill s-invoiced">billed</span>` : ""}</div>
          <div class="row-sub">${esc(j.customers?.full_name ?? "No name")}
            &middot; ${esc(vehicleName(j.vehicles as never))}</div>
        </div>
        <div class="row-side">
          <div class="amount">${lineTotal(j) > 0 ? money(lineTotal(j)) : ""}</div>
          <div class="when">${j.scheduled_at
            ? esc(new Date(j.scheduled_at).toLocaleDateString("en-US",
                { weekday: "short", month: "short", day: "numeric" }))
            : "no date"}</div>
        </div>
      </a>`;

    host.innerHTML = `<div class="card minecard">
      <div class="card-head">On you &middot; ${doing.length}</div>
      <div class="minesum">
        ${late.length ? `<span class="bad"><b>${late.length}</b> past their day</span>` : ""}
        ${unbilled.length ? `<span class="bad"><b>${unbilled.length}</b> finished, not billed</span>` : ""}
        ${quotes.length ? `<span><b>${quotes.length}</b> waiting on a yes</span>` : ""}
        ${owed > 0 ? `<span><b>${money(owed)}</b> billed, not paid</span>` : ""}
        ${!quotes.length && !late.length && !unbilled.length && owed <= 0
          ? `<span>${doing.length ? `Nothing overdue. ${doing.length} open.`
              : "Nothing left on you."}</span>` : ""}
        ${freshDone.length
          ? `<span class="good"><b>${freshDone.length}</b> finished this week</span>` : ""}
      </div>
      <div id="mine-fresh">${
        fresh.length ? fresh.map(line).join("")
          : `<div class="quiet pad-sm">${done.length
              ? "Nothing left on you. What you have finished is below."
              : "Nothing has moved in the last seven days."}</div>`}</div>
      <!-- FINISHED, UNDERNEATH, IN GREEN. Below a divider rather than
           mixed in, because the question this card answers is "what is
           left" and the answer must not be something you have to read
           past four green rows to find. -->
      ${freshDone.length ? `<div class="minedone">
        <div class="minedone-h">Finished &middot; ${freshDone.length}</div>
        ${freshDone.map(line).join("")}
      </div>` : ""}
      <div id="mine-older" hidden>${older.map(line).join("")}</div>
      ${older.length
        ? `<button class="minemore" id="mine-more">Show ${older.length} older
             ${older.length === 1 ? "one" : "ones"}</button>`
        : ""}
    </div>`;

    /**
     * ALREADY DRAWN, JUST HIDDEN. The older ones came back in the same
     * query, so opening them is instant and cannot fail halfway — a
     * "load more" that goes to the network is a button that sometimes
     * does nothing on a phone in a car park.
     */
    void fillThumbs(host);
    document.getElementById("mine-more")?.addEventListener("click", () => {
      const box = document.getElementById("mine-older");
      const btn = document.getElementById("mine-more");
      if (!box || !btn) return;
      box.hidden = false;
      btn.remove();
    });
  } catch {
    // A band about yourself is not worth an error on the shop's desk.
    host.innerHTML = "";
  }
}

/**
 * A QUIET TALLY OF THE DAY.
 *
 * It draws nothing at all until somebody has done something, because a
 * counter reading zero at half past eight in the morning is a reproach
 * nobody asked for. From the first action on it simply says the number,
 * with what it is made of underneath so it can be believed rather than
 * taken on trust.
 *
 * It counts up as the day goes because the desk redraws whenever somebody
 * comes back to it, which is constantly. No polling: a number that ticks
 * on its own while you are reading the screen is the annoying version of
 * this.
 */
async function dayTally(): Promise<void> {
  const host = document.getElementById("tally");
  if (!host) return;
  try {
    const day = await myDay();
    if (!day.total) { host.innerHTML = ""; return; }
    host.innerHTML = `<div class="tally">
      <span class="tally-n">${day.total}</span>
      <span class="tally-w">${day.total === 1 ? "thing" : "things"} done today</span>
      <span class="tally-of">${day.parts
        .map((p) => `${p.n} ${esc(p.what)}`).join(" &middot; ")}</span>
    </div>`;
  } catch {
    // A tally is not worth an error over the shop's dashboard.
    host.innerHTML = "";
  }
}
